import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import useHome from './useHome';
import { PIN_KIND } from '../services/favorites';
import { newItemPath } from '../components/items/FavoriteCardButtons';
import { launcherDescription, pinDescription } from '../components/home/homePins';
import useOpenPin from '../components/home/useOpenPin';
import { normalizeId } from '../utils/ids';

// With an empty query the palette leads with this many pins; the rest (and
// every favorite card) wait for a typed query.
const SHOWN_WHEN_EMPTY = 5;

/**
 * The command palette's "do it from anywhere" rows beyond the personal
 * collection's Add item: every pin on Home's ONE Pinned strip (KPR-1123) — a
 * quick add, a multi add, an add-item card, a dashboard, a record, a tag
 * page, a combined list — in the person's one order, worded and opened by
 * the same functions as its cell on Home (homePins); then every favorite card
 * the person can add to, in any collection.
 *
 * Reads GET /api/home only once the palette is open (`enabled`).
 */
export default function useLauncherPaletteActions({ enabled = false } = {}) {
	const navigate = useNavigate();
	const open = useOpenPin();
	const { home } = useHome({ enabled });

	return useMemo(() => {
		if (!home) return [];
		const actions = [];
		const seen = new Set();
		(home.pinned || []).forEach((pin, index) => {
			if (pin.targetType === PIN_KIND.ADD_ITEM) {
				seen.add(`${normalizeId(pin.collection_id)}:${normalizeId(pin.target_id)}`);
			}
			actions.push({
				key: `home-pin-${normalizeId(pin._id)}`,
				title: pinDescription(pin),
				run: () => open(pin),
				onlyWhenTyped: index >= SHOWN_WHEN_EMPTY,
			});
		});
		(home.favoriteCards || []).forEach(fav => {
			const collectionId = normalizeId(fav.collection_id);
			const cardId = normalizeId(fav.target_id);
			const key = `${collectionId}:${cardId}`;
			if (!collectionId || !cardId || seen.has(key)) return;
			seen.add(key);
			actions.push({
				key: `favorite-card-${key}`,
				// The same words a pinned add-item card uses.
				title: launcherDescription({ ...fav, targetType: PIN_KIND.ADD_ITEM }),
				run: () => navigate(newItemPath(collectionId, cardId)),
				onlyWhenTyped: true,
			});
		});
		return actions;
	}, [home, navigate, open]);
}
